import React from 'react';

/**
 * 6love FormStreak — recent form as a row of small W/L/T chips with overflow.
 * The first letter of each result is read: "W" = win (green), "L" = loss (red),
 * anything else = tie (neutral). The product re-implemented this inline on the
 * flight board; this is the shared version.
 *
 * App implementation of the design system's `FormStreak`.
 */
interface FormStreakProps {
  /** Results, e.g. ['W','L','W']. Order controlled by `order`. */
  form: string[];
  /** Max chips before collapsing to "+N". @default 6 */
  max?: number;
  /**
   * Display order. `"recent-first"` (default) reverses the array so the newest
   * result is leftmost; `"chrono"` renders the array as given.
   */
  order?: 'recent-first' | 'chrono';
  /** @default "md" */
  size?: 'sm' | 'md';
  className?: string;
}

const SIZE = {
  sm: 'w-3.5 h-3.5 text-[9px]',
  md: 'w-5 h-5 text-[11px]',
} as const;

const chipClass = (result: string, size: 'sm' | 'md'): string => {
  const letter = (result || '').charAt(0).toUpperCase();
  const tone =
    letter === 'W'
      ? 'bg-green-200 text-green-900 dark:bg-green-700 dark:text-green-50'
      : letter === 'L'
        ? 'bg-red-200 text-red-900 dark:bg-red-700 dark:text-red-50'
        : 'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-100';
  return `inline-flex items-center justify-center font-bold rounded-sm ${SIZE[size]} ${tone}`;
};

const titleFor = (result: string): string => {
  const letter = (result || '').charAt(0).toUpperCase();
  return letter === 'W' ? 'Win' : letter === 'L' ? 'Loss' : 'Tie';
};

const FormStreak: React.FC<FormStreakProps> = ({
  form,
  max = 6,
  order = 'recent-first',
  size = 'md',
  className,
}) => {
  if (!form || form.length === 0) {
    return <span className="text-gray-400 dark:text-gray-500">—</span>;
  }

  const ordered = order === 'recent-first' ? [...form].slice().reverse() : form;
  const shown = ordered.slice(0, max);
  const overflow = ordered.length - shown.length;

  return (
    <span className={`inline-flex items-center gap-0.5${className ? ` ${className}` : ''}`}>
      {shown.map((r, idx) => (
        <span key={idx} className={chipClass(r, size)} title={titleFor(r)}>
          {(r || '').charAt(0).toUpperCase()}
        </span>
      ))}
      {overflow > 0 && (
        <span className="ml-0.5 text-[11px] font-medium text-gray-500 dark:text-gray-400">
          +{overflow}
        </span>
      )}
    </span>
  );
};

export default FormStreak;
